import React from 'react';
import { ShieldCheck, Clock, Users, Zap, TrendingUp, BarChart3, Database, Lock, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import FadeIn from '../components/FadeIn';

// Fixed import: variable names cannot have spaces
import secureHealthcareIT from '../assets/images/642922.png';

const WhyUs = () => {
  return (
    <div className="bg-white">
      {/* 1. BRAND AUTHORITY HERO */}
      <section className="py-24 bg-slate-900 text-white px-8 relative overflow-hidden">
        {/* Decorative Background Element */}
        <motion.div 
          animate={{ opacity: [0.1, 0.2, 0.1], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute -top-24 -right-24 w-96 h-96 bg-[#14b8a6] rounded-full blur-[120px]"
        />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative z-10">
            <FadeIn direction="right">
              <h2 className="text-[#14b8a6] font-bold tracking-widest uppercase text-sm mb-4">The OptiRev Advantage</h2>
              <h1 className="text-4xl md:text-6xl font-black mb-8 leading-tight">
                Where Financial <span className="text-[#14b8a6]">Excellence</span> Meets IT Innovation.
              </h1>
              <p className="text-xl text-slate-400 mb-8 leading-relaxed">
                We don't just process claims; we modernize your entire practice infrastructure to ensure 
                long-term financial health and technological security.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm backdrop-blur-md">
                  <ShieldCheck className="text-[#14b8a6]" size={18} /> HIPAA Compliant
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm backdrop-blur-md">
                  <Lock className="text-[#14b8a6]" size={18} /> HITECH Standards
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="relative hidden lg:block">
            <FadeIn direction="left" delay={0.3}>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 p-8 rounded-3xl border border-white/10 mt-12 hover:bg-white/10 transition-colors">
                  <p className="text-5xl font-black text-[#14b8a6] mb-2">98%</p>
                  <p className="text-slate-400 text-xs uppercase tracking-widest font-bold">Clean Claim Rate</p>
                </div>
                <div className="bg-[#14b8a6] p-8 rounded-3xl shadow-2xl hover:scale-105 transition-transform">
                  <p className="text-5xl font-black text-white mb-2">70%</p>
                  <p className="text-white/80 text-xs uppercase tracking-widest font-bold">Lower IT Costs</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 2. THE PROACTIVE PARTNERSHIP MODEL */}
      <section className="py-24 px-8 max-w-7xl mx-auto">
        <FadeIn direction="down">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e3a8a] mb-4">Proactive Partnership</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">We operate as an extension of your team, not just a service provider.</p>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              t: "Dedicated Account Managers",
              d: "Personalized attention ensuring your billing concerns are addressed instantly.",
              icon: <Users className="text-[#14b8a6]" size={32} />
            },
            {
              t: "24/7 Technical Support",
              d: "Your practice never stops, and neither do we. Expert IT support whenever you need it.",
              icon: <Clock className="text-[#14b8a6]" size={32} />
            },
            {
              t: "Seamless Integration",
              d: "We bridge the gap between EHR/EMR systems and RCM workflows.",
              icon: <Zap className="text-[#14b8a6]" size={32} />
            }
          ].map((item, i) => (
            <FadeIn key={i} delay={i * 0.2}>
              <div className="p-10 rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 group cursor-default h-full">
                <div className="mb-6 group-hover:scale-110 transition-transform duration-300">{item.icon}</div>
                <h4 className="text-xl font-bold text-[#1e3a8a] mb-4 group-hover:text-[#14b8a6] transition-colors">{item.t}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{item.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* 3. SECURITY & INTEROPERABILITY FOCUS */}
      <section className="py-24 bg-[#f8fafc] px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn direction="right">
            <div className="relative rounded-[3rem] overflow-hidden shadow-2xl group">
              <img src={secureHealthcareIT} alt="Secure Healthcare IT" className="w-full h-auto group-hover:scale-105 transition-transform duration-700" />
            </div>
          </FadeIn>
          
          <div>
            <FadeIn direction="left">
              <h2 className="text-4xl font-bold text-[#1e3a8a] mb-8">Modernizing Practice Operations</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Most billing companies struggle with fragmented systems. Our specialized IT solutions solve 
                the challenges of interoperability, ensuring data flows securely and accurately.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4 p-6 bg-white rounded-2xl border-l-4 border-[#14b8a6] shadow-sm hover:shadow-md transition-shadow">
                  <Database className="text-[#14b8a6] shrink-0" />
                  <div>
                    <h5 className="font-bold text-[#1e3a8a]">Unified Data Flow</h5>
                    <p className="text-sm text-slate-500">Eliminating the bottleneck between clinical data and billing.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-6 bg-white rounded-2xl border-l-4 border-[#14b8a6] shadow-sm hover:shadow-md transition-shadow">
                  <ShieldCheck className="text-[#14b8a6] shrink-0" />
                  <div>
                    <h5 className="font-bold text-[#1e3a8a]">HIPAA-Managed IT</h5>
                    <p className="text-sm text-slate-500">Rigorous security protocols that go beyond standard software.</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 4. QUANTIFIABLE PROOF */}
      <section className="py-24 px-8 text-center max-w-5xl mx-auto">
        <FadeIn direction="up">
          <h2 className="text-3xl font-bold text-[#1e3a8a] mb-16 italic font-serif">"Our mission is to merge established RCM strengths with distinct IT value."</h2>
        </FadeIn>
        
        <div className="grid md:grid-cols-2 gap-12">
          <FadeIn delay={0.2} direction="right">
            <div className="bg-white p-12 rounded-[2rem] shadow-xl border border-[#14b8a6]/10 hover:border-[#14b8a6] transition-colors">
              <h3 className="text-6xl font-black text-[#14b8a6] mb-4">+30%</h3>
              <p className="text-xl font-bold text-[#1e3a8a]">Revenue Increase Per Patient</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.4} direction="left">
            <div className="bg-white p-12 rounded-[2rem] shadow-xl border border-[#14b8a6]/10 hover:border-[#14b8a6] transition-colors">
              <h3 className="text-6xl font-black text-[#1e3a8a] mb-4">+60%</h3>
              <p className="text-xl font-bold text-[#14b8a6]">Claim Approval Rate</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 5. FINAL CONVERSION SECTION */}
      <section className="py-24 bg-[#1e3a8a] px-8 text-center text-white relative overflow-hidden">
        <motion.div 
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 5, repeat: Infinity }}
          className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#14b8a6] rounded-full blur-[120px]"
        />
        
        <div className="max-w-3xl mx-auto relative z-10">
          <FadeIn>
            <h2 className="text-4xl font-bold mb-8 leading-tight">Ready for a True Partnership?</h2>
            <p className="text-xl text-slate-300 mb-10">Schedule your professional revenue assessment and IT audit today.</p>
            <Link 
              to="/contact" 
              className="bg-[#14b8a6] text-white px-12 py-5 rounded-2xl font-bold text-lg hover:shadow-2xl transition-all inline-block hover:-translate-y-2 active:scale-95 flex items-center gap-2 mx-auto w-fit"
            >
              Get Started with OptiRev <ChevronRight size={20} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;